import { DocumentViewer, Spinner, BaseButton } from '../../../..';
import React, { useEffect, useRef, useState } from 'react';
import { IPaymentDetailProps } from './payment-detail.types';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';
import { TextRow, DateRow, AmountRow, ElementRow } from '../timeline-row-components';

export const PaymentDetail: React.FunctionComponent<IPaymentDetailProps> = (
    props: IPaymentDetailProps,
): React.ReactElement => {
    const {
        payment,
        appType,
        onLoadPaymentDocumentUrl,
        eraPayerName,
        eraContractName,
        eraGroupId,
        crossoverCarrier,
        loading: externalLoading = false,
    } = props;

    const onLoadDocumentRef = useRef(onLoadPaymentDocumentUrl);
    onLoadDocumentRef.current = onLoadPaymentDocumentUrl;

    const [documentLoading, setDocumentLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string | undefined>();
    const [documentLoadFailed, setDocumentLoadFailed] = useState(false);
    const [showDocument, setShowDocument] = useState(false);

    // Provider: Only fetch document preview when user clicks "Show paper EOB" button
    useEffect(() => {
        if (appType !== 'provider' || !payment.id || !onLoadPaymentDocumentUrl || !showDocument) {
            return;
        }

        let cancelled = false;
        setDocumentLoading(true);
        setDocumentLoadFailed(false);
        setDocumentUrl(undefined);

        (async (): Promise<void> => {
            try {
                const load = onLoadDocumentRef.current;
                if (!load) {
                    return;
                }
                const result = await load(payment.id!);
                if (!cancelled) {
                    setDocumentUrl(result?.url);
                }
            } catch {
                if (!cancelled) {
                    setDocumentUrl(undefined);
                    setDocumentLoadFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setDocumentLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [payment.id, appType, showDocument]);

    const showSupportSpinner = appType === 'support' && externalLoading;

    const renderProviderFields = () => (
        <>
            <TextRow label="ID" value={payment.id} />
            <DateRow label="Payment Date" value={payment.paymentDate} shortDate={true} />
            <DateRow label="Received Date" value={payment.receivedDate} />
            <TextRow label="Memo" value={(payment as any).memo} />
            <TextRow label="Payer Control Number" value={payment.payerControlNumber} />
            <TextRow label="Status" value={payment.status} />
            <AmountRow label="Charge Amount" value={payment.chargeAmount} />
            <AmountRow label="Allowed Amount" value={payment.allowedAmount} />
            <AmountRow label="Payer Amount" value={payment.payerAmount} />
            <AmountRow label="Patient Amount" value={payment.patientAmount} />
        </>
    );

    const renderSupportFields = () => {
        const supportPayment = payment as any;

        return (
            <>
                <TextRow label="ID" value={payment.id} />
                <AmountRow label="Payer amount" value={payment.payerAmount} />
                <AmountRow label="Patient amount" value={payment.patientAmount} />
                <AmountRow label="Charge amount" value={payment.chargeAmount} />
                <AmountRow label="Allowed amount" value={payment.allowedAmount} />
                <TextRow label="Payer control number" value={payment.payerControlNumber} />
                <TextRow label="Payment status" value={payment.status} />
                <DateRow label="Payment date" value={payment.paymentDate} shortDate={true} />
                <DateRow label="Received date" value={payment.receivedDate} />
                <TextRow label="ERA Payer Name" value={eraPayerName} />
                <TextRow label="ERA Contract Name" value={eraContractName} />
                <TextRow label="ERA Group Id" value={eraGroupId} />
                {!!supportPayment.parentId && props.children && (
                    <ElementRow label="Parent Payment">{props.children}</ElementRow>
                )}
                <TextRow label="Clearinghouse Crossover Carrier" value={crossoverCarrier} />
            </>
        );
    };

    return (
        <Card.CdPanel $embedded key={payment.id}>
            {showSupportSpinner ? (
                <Spinner />
            ) : (
                <>
                    <Card.CdSectionCard $embedded>
                        <S.ExpandTable expand={true}>
                            <S.ExpandTBody>
                                {appType === 'provider' ? renderProviderFields() : renderSupportFields()}
                            </S.ExpandTBody>
                        </S.ExpandTable>
                    </Card.CdSectionCard>

                    {appType === 'provider' && props.serviceLines && (
                        <Card.CdSectionCard $embedded>{props.serviceLines}</Card.CdSectionCard>
                    )}

                    {appType === 'provider' && onLoadPaymentDocumentUrl && (
                        <Card.CdSectionCard $embedded>
                            {!showDocument && (
                                <BaseButton onClick={() => setShowDocument(true)} variant="outlined" size="small">
                                    Show paper EOB
                                </BaseButton>
                            )}
                            {showDocument && (
                                <>
                                    {documentLoading && <Spinner />}
                                    {!documentLoading && documentLoadFailed && (
                                        <div style={{ color: '#767676', fontSize: 14 }}>
                                            Document preview could not be loaded.
                                        </div>
                                    )}
                                    {!documentLoading && documentUrl && <DocumentViewer url={documentUrl} />}
                                </>
                            )}
                        </Card.CdSectionCard>
                    )}

                    {appType === 'support' && props.serviceLines && (
                        <Card.CdSectionCard $embedded>{props.serviceLines}</Card.CdSectionCard>
                    )}
                </>
            )}
        </Card.CdPanel>
    );
};
